/**
 * Lead Sources — the owner's view of the research seat's lead work.
 *
 * Wire shapes mirror `system/services/lead_sources.py :: serialize_*` (one
 * shape for REST and the `leadsource_*` tools) — change them together.
 */
import { api } from "./client";

export type SourceStatus = "active" | "paused" | "blocked" | "retired";
export type BlockedReason = "robots" | "terms" | "login";
export type CandidateStatus = "proposed" | "interesting" | "not_interesting" | "withdrawn";
export type CandidateFit = "high" | "medium" | "low";
export type HandoffStage = "queued" | "with_seth" | "done" | "failed" | "canceled";
/** `proposed` resets a decision ("Undo") while the hand-off hasn't started. */
export type Verdict = "interesting" | "not_interesting" | "proposed";

export interface LeadSourceLog {
  id: string;
  source_id: string;
  worked_at: string | null;
  summary: string;
  members_seen: number | null;
  new_members: number | null;
  author_user_id: string | null;
}

export interface LinkedTask {
  id: string;
  status: string | null;
  title: string | null;
  completed_at: string | null;
  agent_error: string | null;
}

export interface LeadSource {
  id: string;
  slug: string;
  name: string;
  url: string | null;
  kind: string;
  cadence: string;
  status: SourceStatus;
  blocked_reason: BlockedReason | null;
  manual_review_task_id: string | null;
  manual_review_task: LinkedTask | null;
  note: string | null;
  last_worked_at: string | null;
  overdue: boolean;
  last_log: LeadSourceLog | null;
  counts: { proposed: number; interesting: number; not_interesting: number };
  created_at: string | null;
  updated_at: string | null;
}

export interface LeadCandidate {
  id: string;
  name: string;
  website: string | null;
  host: string | null;
  source_slugs: string[];
  why: string;
  rationale_md: string | null;
  evidence: { url: string; title?: string }[];
  segment: string;
  pipelines: string[];
  fit: CandidateFit;
  status: CandidateStatus;
  verdict_note: string | null;
  decided_at: string | null;
  handoff: { task_id: string; job_id: string; agent_slug: string; not_before: string } | null;
  handoff_task: LinkedTask | null;
  handoff_job: { id: string; status: string; error: string | null } | null;
  handoff_stage: HandoffStage | null;
  crm_hint: {
    companies: { id: string; name: string | null }[];
    contacts: { id: string; name: string | null }[];
    contact_count: number;
  } | null;
  withdraw_reason: string | null;
  created_at: string | null;
}

export interface LeadSourceDetail extends LeadSource {
  logs: LeadSourceLog[];
  candidates: LeadCandidate[];
}

export interface LeadOverview {
  sources_active: number;
  sources_overdue: number;
  sources_blocked: number;
  blocked_awaiting_review: number;
  leads_awaiting: number;
  interesting_30d: number;
  with_seth: number;
}

export interface LeadList {
  items: LeadCandidate[];
  counts: Record<CandidateStatus, number>;
}

export const leadSourcesApi = {
  overview: () => api.get<LeadOverview>("/api/v1/lead-sources/overview"),
  sources: () => api.get<LeadSource[]>("/api/v1/lead-sources/sources"),
  source: (slug: string) =>
    api.get<LeadSourceDetail>(`/api/v1/lead-sources/sources/${encodeURIComponent(slug)}`),
  leads: (params: { status?: string; source?: string } = {}) =>
    api.get<LeadList>("/api/v1/lead-sources/leads", params),
  verdict: (id: string, verdict: Verdict, note?: string) =>
    api.post<LeadCandidate>(`/api/v1/lead-sources/leads/${id}/verdict`, {
      verdict,
      note: note?.trim() || undefined,
    }),
};
